import React, { useContext } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import { PortalDropdownContext } from "./PortalDropdown";

/**
 * A dropdown menu item that handles click events and closes the menu.
 * Designed to be used as a child of PortalDropdown.
 *
 * @param {Object} props
 * @param {string} props.icon - FontAwesome icon class (e.g., "fa-envelope")
 * @param {string} props.label - Text label for the item
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.disabled - Whether the item is disabled
 * @param {boolean} props.active - Whether the item is in active/selected state
 * @param {string} props.className - Extra classes for the item (e.g. `d-lg-none` for responsive hiding)
 */
function DropdownMenuItem({ icon, label, onClick, disabled = false, active = false, className }) {
  const closeMenu = useContext(PortalDropdownContext);

  const handleClick = (e) => {
    e.stopPropagation();

    if (closeMenu) closeMenu();
    if (onClick) onClick(e);
  };

  return (
    <button
      type="button"
      className={classnames("dropdown-item d-flex align-items-center fw-semibold paragraph-sm", className, { active })}
      onClick={handleClick}
      disabled={disabled}
    >
      {/* Disabled items: let the icon inherit the button's disabled grey instead of
          keeping its own color while the label fades */}
      <i className={classnames(icon, "fa-fw me-2", { "text-neutral-60": !disabled })} />
      <span>{label}</span>
    </button>
  );
}

DropdownMenuItem.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  active: PropTypes.bool,
  className: PropTypes.string,
};

export default DropdownMenuItem;
